import { FC, useMemo, useState, CSSProperties } from 'react';
import cn from 'classnames';
import { CaretDownFilled } from '@ant-design/icons';
import { AppSelect } from '../select';
import styles from './tablePagination.module.scss';

type CSSVariableProperties = CSSProperties & Record<string, string | number>;

type TablePaginationProps = {
    currentPage: number;
    pageSize: number;
    totalItems: number;
    pageSizeOptions: string[];
    onChangePage: (page: number) => void;
    onChangePageSize: (size: number) => void;
    previousLabel: string;
    nextLabel: string;
    rangeLabel?: (start: number, end: number, total: number) => string;
    showDocumentsLabel: string;
    pageSizeSelectorWidth?: number | string;
    className?: string;
};

export const TablePagination: FC<TablePaginationProps> = ({
    currentPage,
    pageSize,
    totalItems,
    pageSizeOptions,
    onChangePage,
    onChangePageSize,
    previousLabel,
    nextLabel,
    rangeLabel,
    showDocumentsLabel,
    pageSizeSelectorWidth,
    className,
}) => {
    const [isPageSizeDropdownOpen, setIsPageSizeDropdownOpen] = useState(false);

    const totalPages = useMemo(() => {
        if (!pageSize || Number.isNaN(pageSize)) return 1;
        return Math.max(1, Math.ceil(totalItems / pageSize));
    }, [pageSize, totalItems]);

    const pageItems = useMemo(() => {
        if (!totalPages) return [] as (number | 'ellipsis')[];

        if (totalPages <= 5) {
            return Array.from({ length: totalPages }, (_, index) => index + 1);
        }

        const pages: (number | 'ellipsis')[] = [1];
        const left = Math.max(2, currentPage - 1);
        const right = Math.min(totalPages - 1, currentPage + 1);

        if (left > 2) {
            pages.push('ellipsis');
        }

        for (let page = left; page <= right; page += 1) {
            pages.push(page);
        }

        if (right < totalPages - 1) {
            pages.push('ellipsis');
        }

        pages.push(totalPages);

        return pages;
    }, [currentPage, totalPages]);

    const rangeStart =
        totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const rangeEnd =
        totalItems === 0 ? 0 : Math.min(currentPage * pageSize, totalItems);
    const rangeText =
        rangeLabel?.(rangeStart, rangeEnd, totalItems) ??
        `${rangeStart}-${rangeEnd} of ${totalItems}`;

    const handlePageChange = (page: number) => {
        const nextPage = Math.min(Math.max(1, page), totalPages);
        onChangePage(nextPage);
    };

    const handlePrev = () => {
        if (currentPage <= 1) return;
        handlePageChange(currentPage - 1);
    };

    const handleNext = () => {
        if (currentPage >= totalPages) return;
        handlePageChange(currentPage + 1);
    };

    const handlePageSizeChange = (value: string) => {
        const numericValue = Number(value);

        if (Number.isNaN(numericValue)) return;

        onChangePageSize(numericValue);
        onChangePage(1);
    };

    const resolvedPageSizeSelectorWidth =
        typeof pageSizeSelectorWidth === 'number'
            ? `${pageSizeSelectorWidth}px`
            : pageSizeSelectorWidth;

    const pageSizeSelectStyle: CSSVariableProperties | undefined =
        resolvedPageSizeSelectorWidth
            ? {
                  '--page-size-select-width': resolvedPageSizeSelectorWidth,
                  '--page-size-select-min-width': resolvedPageSizeSelectorWidth,
              }
            : undefined;

    if (totalItems <= 0) return null;

    return (
        <div className={cn(styles.pagination, className)}>
            <div className={styles.paginationControls}>
                <button
                    type="button"
                    onClick={handlePrev}
                    className={cn(
                        styles.paginationArrow,
                        styles.paginationPrev,
                        {
                            [styles.paginationArrowDisabled]:
                                currentPage === 1,
                        }
                    )}
                    disabled={currentPage === 1}
                >
                    <span
                        aria-hidden="true"
                        className={styles.paginationArrowIcon}
                    />
                    <span className={styles.paginationArrowLabel}>
                        {previousLabel}
                    </span>
                </button>
                <div className={styles.paginationPages}>
                    {pageItems.map((page, index) =>
                        page === 'ellipsis' ? (
                            <span
                                className={styles.paginationEllipsis}
                                key={`ellipsis-${index}`}
                            >
                                …
                            </span>
                        ) : (
                            <button
                                type="button"
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={cn(styles.paginationPage, {
                                    [styles.paginationPageActive]:
                                        page === currentPage,
                                })}
                            >
                                {page}
                            </button>
                        )
                    )}
                </div>
                <button
                    type="button"
                    onClick={handleNext}
                    className={cn(
                        styles.paginationArrow,
                        styles.paginationNext,
                        {
                            [styles.paginationArrowDisabled]:
                                currentPage === totalPages,
                        }
                    )}
                    disabled={currentPage === totalPages}
                >
                    <span className={styles.paginationArrowLabel}>
                        {nextLabel}
                    </span>
                    <span
                        aria-hidden="true"
                        className={styles.paginationArrowIcon}
                    />
                </button>
            </div>
            <div className={styles.paginationInfo}>
                <span className={styles.paginationRange}>{rangeText}</span>
                <div className={styles.pageSizeControl}>
                    <span className={styles.pageSizeLabel}>
                        {showDocumentsLabel}
                    </span>
                    <AppSelect
                        dropdownMatchSelectWidth={false}
                        value={pageSize.toString()}
                        className={cn(styles.pageSizeSelect, {
                            [styles.pageSizeSelectOpen]: isPageSizeDropdownOpen,
                        })}
                        popupClassName={styles.pageSizeDropdown}
                        options={pageSizeOptions.map(option => ({
                            value: option,
                            label: option,
                        }))}
                        onChange={handlePageSizeChange}
                        onDropdownVisibleChange={open =>
                            setIsPageSizeDropdownOpen(open)
                        }
                        suffixIcon={
                            <CaretDownFilled className={styles.dropdownArrow} />
                        }
                        style={pageSizeSelectStyle}
                    />
                </div>
            </div>
        </div>
    );
};

export default TablePagination;
